import React, { useState } from 'react';
import { Heart, Users, Mail, Shield, MessageSquare, CheckCircle, ArrowRight, HelpCircle, Map } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Match = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <div className="-mt-16">
      {/* Hero */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-rose-500/12 via-pink-500/8 to-transparent rounded-full blur-[100px]" />
          <div className="absolute -bottom-20 -left-20 w-[500px] h-[500px] bg-gradient-to-tr from-pink-500/8 to-transparent rounded-full blur-[100px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center px-3.5 py-1.5 text-[11px] font-semibold tracking-wide uppercase text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-full mb-6">
            {t('match.inDevelopment')}
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-gradient-hero mb-5">
            {t('match.title')}
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto mb-10">
            {t('match.subtitle')}
          </p>

          <div className="max-w-2xl mx-auto">
            <div className="border-gradient bg-dark-card rounded-2xl p-8 md:p-10 shadow-dark-card">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="bg-dark-surface border border-dark-border rounded-xl p-4 flex-1">
                  <div className="text-sm text-gray-500 mb-2">寻找</div>
                  <input type="text" placeholder="理想对象..."
                    className="w-full bg-transparent border-none text-white placeholder-gray-600 text-lg font-medium outline-none" />
                </div>
                <Heart className="h-8 w-8 text-rose-400" />
                <div className="bg-dark-surface border border-dark-border rounded-xl p-4 flex-1">
                  <div className="text-sm text-gray-500 mb-2">喜欢</div>
                  <input type="text" placeholder="兴趣爱好..."
                    className="w-full bg-transparent border-none text-white placeholder-gray-600 text-lg font-medium outline-none" />
                </div>
              </div>
              <button className="bg-white text-dark px-8 py-4 rounded-full font-bold text-lg hover:shadow-glow-white transition-all duration-400 inline-flex items-center">
                开始匹配 <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-dot-pattern bg-dot opacity-15" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-14">如何工作</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { num: "1", title: "告诉AI你的偏好", desc: "描述你理想的伴侣类型，AI会为你找到最匹配的人", icon: MessageSquare },
              { num: "2", title: "AI安排约会计划", desc: "AI会通过邮件发送约会计划，包括时间、地点和匹配信息", icon: Mail },
              { num: "3", title: "直接见面", desc: "无需无休止的聊天，直接见面，享受真实的交流", icon: Heart },
            ].map((step, i) => (
              <div key={i} className="bg-dark-card border border-dark-border rounded-2xl p-6 text-center glow-card hover:border-dark-border-light transition-all duration-400">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-dark-surface border border-dark-border rounded-full mb-4">
                  <step.icon className="h-7 w-7 text-accent" />
                </div>
                <div className="text-3xl font-bold text-gradient-blue mb-2">{step.num}</div>
                <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-gray-400 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-gradient bg-dark-card rounded-2xl shadow-dark-card p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-white mb-4">1000次约会模拟<br />只为让你遇到对的人</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div><div className="text-4xl font-bold text-gradient-blue mb-2">1000+</div><div className="text-gray-400">已安排约会</div></div>
              <div><div className="text-4xl font-bold text-gradient-blue mb-2">95%</div><div className="text-gray-400">获得约会（Tinder为25%）</div></div>
              <div><div className="text-4xl font-bold text-gradient-blue mb-2">90%</div><div className="text-gray-400">第一次约会后希望第二次约会</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-14">只享受有趣的部分</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-dark-card border border-dark-border rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">其他交友App</h3>
              <div className="space-y-3">
                {["无休止的滑动", "琐碎的聊天", "难以真正了解对方"].map((item, i) => (
                  <div key={i} className="flex items-start"><div className="w-5 h-5 rounded-full bg-dark-surface mr-3 mt-0.5 flex-shrink-0" /><span className="text-gray-400">{item}</span></div>
                ))}
              </div>
            </div>
            <div className="border-gradient bg-dark-card rounded-2xl p-8 shadow-dark-card">
              <h3 className="text-xl font-bold text-white mb-4">华人交友</h3>
              <div className="space-y-3">
                {["1个准备就绪的约会邀请", "AI智能匹配", "真实见面，真诚交流"].map((item, i) => (
                  <div key={i} className="flex items-start"><CheckCircle className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-accent" /><span className="text-gray-300">{item}</span></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safety */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-dot-pattern bg-dot opacity-15" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-4">我们的安全措施</h2>
          <p className="text-center text-gray-400 mb-14">华人交友专为创造一个安全舒适的空间而设计</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: "实名验证", desc: "所有用户都需要验证身份，确保真实性", icon: CheckCircle },
              { title: "安全提示", desc: "约会前提供安全建议和注意事项", icon: Shield },
              { title: "安全场所", desc: "精心选择约会地点，确保安全和舒适", icon: Map },
              { title: "随时离开", desc: "如果感到不适，可以随时离开", icon: ArrowRight },
              { title: "禁止幽灵", desc: "无故爽约的用户将被禁止使用", icon: Users },
              { title: "支持团队", desc: "专业支持团队随时为你提供帮助", icon: Heart },
            ].map((item, i) => (
              <div key={i} className="bg-dark-card border border-dark-border rounded-2xl p-6 glow-card hover:border-dark-border-light transition-all duration-400">
                <div className="w-11 h-11 bg-dark-surface border border-dark-border rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="h-5 w-5 text-accent" />
                </div>
                <h3 className="text-base font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-14">用户评价</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { name: "Alex Chen", loc: "纽约，软件工程师", text: "约会很棒，我们聊了很多。AI的匹配确实很准确，期待更多匹配！" },
              { name: "Sophie Li", loc: "洛杉矶，研究生", text: "几周前匹配成功，现在我们已经确定了关系。感谢这个平台！" },
              { name: "David Wang", loc: "旧金山，数据分析师", text: "约会很顺利，我能看到AI发现的我们的相似之处。非常满意！" },
              { name: "Emma Zhang", loc: "波士顿，教师", text: "约会太棒了！AI真的找到了合适的匹配。我们计划再次见面！" },
              { name: "Ryan Liu", loc: "芝加哥，设计师", text: "第一次使用这个平台，体验非常好。直接见面比聊天更有效率。" },
              { name: "Olivia Zhao", loc: "西雅图，产品经理", text: "通过朋友介绍了解到这个平台。约会很愉快，对方人很好。强烈推荐！" },
            ].map((t, i) => (
              <div key={i} className="bg-dark-card border border-dark-border rounded-2xl p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-full bg-dark-surface border border-dark-border flex items-center justify-center text-white font-bold text-sm mr-3">
                    {t.name.charAt(0)}
                  </div>
                  <div><div className="font-semibold text-white text-sm">{t.name}</div><div className="text-xs text-gray-500">{t.loc}</div></div>
                </div>
                <p className="text-gray-400 text-sm italic">"{t.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-dot-pattern bg-dot opacity-15" />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-14">常见问题</h2>
          <div className="space-y-4">
            {[
              { q: "华人交友如何工作？", a: "华人交友为你安排约会，无需滑动或聊天。提交信息后，AI会通过邮件发送约会计划，包括时间、地点和匹配对象的信息。约会地点通常在安全的公共场所。" },
              { q: "约会前我能了解匹配对象什么信息？", a: "找到匹配对象后，你会收到一个包含对方照片和简短说明的卡片，解释为什么你们会是很好的配对。你还会收到一个时间表来分享你本周的空闲时间。双方填写后，我们会安排约会时间、地点，并提供一些约会建议。" },
              { q: "如果临时不能赴约怎么办？", a: "如果你真的临时不能赴约，请尽快联系对方取消，以免被禁止使用平台。" },
              { q: "约会地点在哪里？", a: "约会地点在精心选择的公共场所，确保安全和愉快的体验。" },
            ].map((item, i) => (
              <div key={i} className="bg-dark-card border border-dark-border rounded-2xl p-6">
                <h3 className="text-base font-semibold text-white mb-2 flex items-center">
                  <HelpCircle className="h-4 w-4 text-accent mr-2 flex-shrink-0" />{item.q}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed ml-6">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-[#d62d20]/08 via-[#ffa700]/08 to-[#4796E3]/08 rounded-full blur-[100px]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">找到约会对象是AI的工作<br />你的工作是享受约会</h2>
          <p className="text-lg text-gray-400 mb-12 max-w-2xl mx-auto">
            加入等待列表，成为首批体验AI驱动的华人交友平台的用户
          </p>
          <form onSubmit={handleSubmit} className="max-w-md mx-auto">
            <div className="flex gap-3">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="输入你的邮箱..."
                className="flex-1 px-4 py-3 bg-dark-card border border-dark-border text-white placeholder-gray-500 rounded-xl outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/50 transition-all duration-300" required />
              <button type="submit"
                className="bg-white text-dark px-6 py-3 rounded-full font-bold hover:shadow-glow-white transition-all duration-400 whitespace-nowrap">
                {submitted ? '已提交！' : '加入等待列表'}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Match;
